import * as Cesium from "cesium";
import { Color } from "react-color";
import { isNumber } from "~/utils/isNumber";

export const ColoringBlendingModes = ["step", "smooth"] as const;
export const ColoringValueModes = ["percentage", "absolute"] as const;
export type ColoringBlendingMode = typeof ColoringBlendingModes[number];
export type ColoringValueMode = typeof ColoringValueModes[number];

export type ColorAndStep = {
  index: number;
  color: Color;
  isLowest: boolean;
  isHighest: boolean;
  toBeDeleted?: boolean;
} & Step;

export type Step = {
  stepMin: number;
  stepMax: number;
  stepMinAbs?: number;
  stepMaxAbs?: number;
};

export const DEFAULT_COLORS: Color[] = [
  "#b600d0",
  "#8600d1",
  "#5000d3",
  "#1900d5",
  "#0057d9",
  "#018cda",
  "#00c7dc",
  "#00deb8",
  "#00e081",
  "#02e247",
  "#02e411",
  "#2ee502",
  "#6ae701",
  "#a7e901",
  "#e5eb02",
  "#edb903",
  "#ef7e03",
  "#f04002",
  "#f20402",
  "#f4023d",
];

export const DEFAULT_BLENDING_MODE: ColoringBlendingMode = "smooth";
export const DEFAULT_VALUE_MODE: ColoringValueMode = "absolute";

export const DEFAULT_STEPS: Step[] = generateDefaultSteps();

export const DEFAULT_COLORS_AND_STEPS = generateDefaultColorsAndSteps();

export type ColorSettings = {
  conditions: ColorCondition[];
};

export type CesiumPointSettings = {
  color: ColorSettings;
  defines: {
    [key: string]: string;
  };
};

export type ColorCondition = [string, string];

export type SavedElevationSettings = {
  colorsAndSteps: ColorAndStep[];
  blendingMode: ColoringBlendingMode;
  valueMode: ColoringValueMode;
  minZ: number;
  maxZ: number;
};
export type SavedIntensitySettings = {
  colorsAndSteps: ColorAndStep[];
  blendingMode: ColoringBlendingMode;
  valueMode: ColoringValueMode;
  minIntensity: number;
  maxIntensity: number;
};

function generateDefaultColorsAndSteps(): ColorAndStep[] {
  const items = DEFAULT_COLORS.map(
    (c, i): ColorAndStep => ({
      index: i,
      color: c,
      stepMin: DEFAULT_STEPS[i].stepMin,
      stepMax: DEFAULT_STEPS[i].stepMax,
      isLowest: false,
      isHighest: false,
    }),
  );
  const complete = addFirstAndLastItems(items);
  return updateColoringSteps(complete);
}

function generateDefaultSteps(): Step[] {
  const retVal: Step[] = [];
  const stepCount = DEFAULT_COLORS.length;
  const minPctVal = 0;
  const maxPctVal = 100;
  const singleStepVal = (maxPctVal - minPctVal) / stepCount;
  for (let i = 0; i < stepCount; i++) {
    retVal.push({
      stepMin: i * singleStepVal,
      stepMax: (i + 1) * singleStepVal,
    });
  }
  return retVal;
}

function addFirstAndLastItems(items: ColorAndStep[]): ColorAndStep[] {
  const copy = items.map((x) => ({ ...x }));
  if (items.length < 1) return copy;
  copy.unshift({
    index: 0,
    color: items[0]?.color ?? "#fff",
    stepMin: Number.NEGATIVE_INFINITY,
    stepMax: items[0]?.stepMin,
    isLowest: true,
    isHighest: false,
  });
  const lastItem = items[items.length - 1];
  copy.push({
    index: items.length - 1,
    color: lastItem?.color ?? "#fff",
    stepMin: lastItem.stepMax,
    stepMax: Number.POSITIVE_INFINITY,
    isHighest: true,
    isLowest: false,
  });
  return copy;
}

export function updateColoringSteps(
  items: ColorAndStep[],
  min?: number,
  max?: number,
): ColorAndStep[] {
  const initialCopy = items.map((x) => ({ ...x }));
  // Reverse array if required
  const firstIndex = initialCopy.findIndex((c) => c.isLowest);
  const lastIndex = initialCopy.findIndex((c) => c.isHighest);
  const copy = firstIndex > lastIndex ? initialCopy : initialCopy.reverse();
  // Adjust step values to match the values of next/previous
  for (let i = 0; i < copy.length; i++) {
    const c = copy[i];
    const higherVal = copy[i - 1];
    const lowerVal = copy[i + 1];
    if (!c.isHighest && !c.isLowest) c.stepMax = higherVal.stepMin;
    if (!c.isLowest && !c.isHighest) c.stepMin = lowerVal.stepMax;
    if (c.isHighest) c.stepMax = Number.POSITIVE_INFINITY;
    if (c.isLowest) c.stepMin = Number.NEGATIVE_INFINITY;
    if (isNumber(min) && isNumber(max)) {
      const absRange = max! - min!;
      c.stepMaxAbs = (c.stepMax / 100) * absRange + min!;
      c.stepMinAbs = (c.stepMin / 100) * absRange + min!;
    }
    c.index = i;
  }
  return copy;
}

export function calculateStepAbsVals(
  items: ColorAndStep[],
  min: number,
  max: number,
): ColorAndStep[] {
  const copy = items.map((x) => ({ ...x }));
  for (let i = 0; i < copy.length; i++) {
    const c = copy[i];
    const absRange = max - min;
    c.stepMaxAbs = (c.stepMax / 100) * absRange + min;
    c.stepMinAbs = (c.stepMin / 100) * absRange + min;
    c.index = i;
  }
  return copy;
}

// Add a new item to the list, use half of the next (lower) value range
export function addNewColorAndStep(
  items: ColorAndStep[],
  addAfter: ColorAndStep | undefined,
): ColorAndStep[] {
  const copy = items.map((x) => ({ ...x }));
  // Find abs min/max
  const absMax = copy.reduce((tot, cur) =>
    isNumber(cur.stepMaxAbs) &&
    !cur.isHighest &&
    !cur.isLowest &&
    (cur.stepMaxAbs! > (tot.stepMaxAbs ?? Number.NEGATIVE_INFINITY) ||
      tot.stepMaxAbs === Number.POSITIVE_INFINITY)
      ? cur
      : tot,
  )?.stepMaxAbs;
  const absMin = copy.reduce((tot, cur) =>
    isNumber(cur.stepMinAbs) &&
    !cur.isHighest &&
    !cur.isLowest &&
    (cur.stepMinAbs! < (tot.stepMinAbs ?? Number.POSITIVE_INFINITY) ||
      tot.stepMinAbs === Number.NEGATIVE_INFINITY)
      ? cur
      : tot,
  )?.stepMinAbs;

  // if addAfter is undef, add right after the highest value
  if (!addAfter) {
    const highestIndex = copy.findIndex((c) => c.isHighest);
    if (highestIndex > -1) {
      const next = copy[highestIndex + 1];
      if (next) {
        const nextRange = next.stepMax - next.stepMin;
        const nextPrevMax = next.stepMax;
        next.stepMax = next.stepMin + nextRange / 2;
        copy.splice(highestIndex + 1, 0, {
          index: highestIndex + 1,
          color: next.color,
          isLowest: false,
          isHighest: false,
          stepMin: next.stepMax,
          stepMax: nextPrevMax,
        });
      }
    }
  } else {
    const addIndex = copy.findIndex((c) => c.index === addAfter.index);
    if (addIndex > -1) {
      // If adding a new lowest item, use range from previous item instead
      if (addIndex === copy.length - 2) {
        const prev = copy[addIndex];
        if (prev) {
          const prevRange = prev.stepMax - prev.stepMin;
          const prevPrevMin = prev.stepMin;
          prev.stepMin = prev.stepMax - prevRange / 2;
          copy.splice(addIndex + 1, 0, {
            index: addIndex + 1,
            color: prev.color,
            isLowest: false,
            isHighest: false,
            stepMin: prevPrevMin,
            stepMax: prev.stepMin,
          });
        }
      } else {
        const next = copy[addIndex + 1];
        if (next) {
          const nextRange = next.stepMax - next.stepMin;
          const nextPrevMax = next.stepMax;
          next.stepMax = next.stepMin + nextRange / 2;
          copy.splice(addIndex + 1, 0, {
            index: addIndex + 1,
            color: next.color,
            isLowest: false,
            isHighest: false,
            stepMin: next.stepMax,
            stepMax: nextPrevMax,
          });
        }
      }
    }
  }
  return updateColoringSteps(copy, absMin, absMax);
}

export type AdjustStepLimitsArgs = {
  items: ColorAndStep[];
  indexToEdit: ColorAndStep["index"];
  newHighValue: number;
  newLowValue: number;
  absMin: number;
  absMax: number;
};
export function adjustStepLimits({
  items,
  indexToEdit,
  newHighValue,
  newLowValue,
  absMin,
  absMax,
}: AdjustStepLimitsArgs): ColorAndStep[] {
  const copy = items.map((x) => ({ ...x }));
  // Find the item that is being edited
  const foundItem = items.find((x) => x.index === indexToEdit);
  if (!foundItem) return copy;
  const itemToEdit = { ...foundItem };

  // Adjust editing item first
  for (let i = 0; i < copy.length; i++) {
    const c = copy[i];
    if (c.index === itemToEdit.index) {
      c.stepMin = newLowValue;
      itemToEdit.stepMin = newLowValue;
      c.stepMax = newHighValue;
      itemToEdit.stepMax = newHighValue;
    }
  }

  // Adjust other items based on currently edited item values
  for (let i = 0; i < copy.length; i++) {
    const c = copy[i];

    c.toBeDeleted = false;

    // Items below (greater index) edited item that have greater min values should be removed
    if (c.index > itemToEdit.index && c.stepMin >= itemToEdit.stepMin) {
      c.toBeDeleted = true;
      continue;
    }
    // Items above (lesser index) exited that have lower max value should be removed
    if (c.index < itemToEdit.index && c.stepMax <= itemToEdit.stepMax) {
      c.toBeDeleted = true;
      continue;
    }

    // Adjust lower limit of items above (lesser index) current item if needed
    if (
      c.index < itemToEdit.index &&
      itemToEdit.stepMax >= c.stepMin &&
      itemToEdit.stepMax <= c.stepMax
    ) {
      c.stepMin = itemToEdit.stepMax;
    }

    // Adjust higher limit of items below (greater index) current item if needed
    if (
      c.index > itemToEdit.index &&
      itemToEdit.stepMin >= c.stepMin &&
      itemToEdit.stepMin <= c.stepMax
    ) {
      c.stepMax = itemToEdit.stepMin;
    }

    // When increasing lower limit, adjust the next lesser items higher limit
    if (c.index === itemToEdit.index + 1 && c.stepMax < itemToEdit.stepMin) {
      c.stepMax = itemToEdit.stepMin;
    }
    // When decreasing higher limit, adjust the next greater item lower limit
    if (c.index === itemToEdit.index - 1 && c.stepMin > itemToEdit.stepMax) {
      c.stepMin = itemToEdit.stepMax;
    }
  }
  return calculateStepAbsVals(copy, absMin, absMax);
}

export function colorsAndStepsToCesium(
  items: ColorAndStep[],
  coloringType: "elevation" | "intensity",
  blendingMode: ColoringBlendingMode,
): CesiumPointSettings {
  const colorSettings: ColorSettings = { conditions: [] as ColorCondition[] };
  const valueStr = coloringType === "elevation" ? `\${Z}` : `\${Intensity}`;
  const defines = {} as CesiumPointSettings["defines"];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.isLowest || item.isHighest) continue;
    if (!isNumber(item.stepMinAbs) || !isNumber(item.stepMaxAbs)) continue;

    const higherVal = { ...items[i - 1] };
    const lowerVal = { ...items[i + 1] };

    if (higherVal.isHighest) higherVal.color = item.color;
    if (lowerVal.isLowest) lowerVal.color = item.color;

    // For some reason, need to keep these in defines for now
    defines.zScale = `1`;
    defines.intensityScale = `1`;

    const colVec4 = Cesium.Color.fromCssColorString(item.color.toString());
    const lColVec4 = Cesium.Color.fromCssColorString(lowerVal.color.toString());

    // Mix down from high limit
    // Scale should be 0 at highLimit and 1 at low limit
    const mixColor = `vec4${lColVec4.toString()}`;
    const mixScale = `(${item.stepMaxAbs}-${valueStr})/(${item.stepMaxAbs}-${item.stepMinAbs})`;

    colorSettings.conditions.push([
      `${valueStr} <= ${item.stepMaxAbs} && ${valueStr} >= ${item.stepMinAbs}`,
      `${
        blendingMode === "step"
          ? `color('${item.color}')`
          : `mix(vec4${colVec4.toString()}, ${mixColor}, clamp(${mixScale}, 0, 1))`
      }`,
    ]);
  }
  // Fallback condition
  colorSettings.conditions.push(["true", "color('#FFFFFF')"]);
  return { color: colorSettings, defines };
}
